import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pathname } from 'Routes';
import { saveUserOccasions } from 'Api/apiFunctions';
import styles from './index.module.css';

// Reasons a new user is joining Projector — drives the purpose-selection step.
// Keep this at 9 items so the 3x3 grid layout and fade-in timing stay intact.
const OCCASIONS = [
  'Family Memories',
  'Home Videos',
  'Photos & Albums',
  'Special Events',
  'Travel & Trips',
  'Digitizing Old Media',
  'Sharing With Family',
  'Family Stories',
  'Just Exploring',
];

// Occasion-selection step for the sign-up flow. Same UI as the gift flow's
// "Is This For A Special Occasion?" step (grey buttons, blue when selected).
// On Continue we proceed to the gift-content page (the Welcome/Congrats screen
// is intentionally disconnected for now). Because this is a brand-new normal
// user — not a gift recipient — they have no uploaded content yet, so we land
// them straight on the upload grid via the `newUserUpload` route flag.
export const ChooseOccasion = () => {
  const navigate = useNavigate();
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  // Reveal the Continue button only after the occasion buttons finish their
  // fade-in, mirroring the gift flow. Last button (index 8) delay 2.4s +
  // 1.6s animation; gift flow uses a 2900ms timer.
  const [gridAnimationComplete, setGridAnimationComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setGridAnimationComplete(true), 2900);
    return () => clearTimeout(timer);
  }, []);

  const toggleOccasion = (occasion) => {
    setSelectedOccasions((prev) => (prev.includes(occasion)
      ? prev.filter((occ) => occ !== occasion)
      : [...prev, occasion]));
  };

  const handleContinue = () => {
    // Keep the selection available for later steps without changing the flow.
    try {
      sessionStorage.setItem('selectedOccasions', JSON.stringify(selectedOccasions));
      // Marks the gift-content page as the new normal-user signup upload flow
      // (survives refresh, unlike location.state) so it shows the placeholder
      // folder / gift-style upload view rather than the generic progress tab.
      sessionStorage.setItem('newUserUploadFlow', 'true');
    } catch (e) {
      // sessionStorage may be unavailable (private mode) — selection is optional.
    }
    // Persist the picked occasions to the backend. Fire-and-forget: we do NOT
    // await it or block navigation on it — the endpoint may not exist yet, and
    // a failure here must never stop the user moving to the upload step.
    saveUserOccasions({ occasions: selectedOccasions })
      .then(() => console.log('[CHOOSE_OCCASION] Occasions saved:', selectedOccasions))
      .catch((err) => console.warn('[CHOOSE_OCCASION] Failed to save occasions (non-blocking):', err));
    navigate(Pathname.giftFlow.content, { state: { newUserUpload: true } });
  };

  return (
    <div className={styles.page}>
      {/* Back to the avatar step. Carry fromVerificationCode so the avatar's
          Continue routes forward to this occasion page again (keeps the flow).
          Styled to match the /gift-content BACK link. */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Back"
        className={styles.backLink}
        onClick={() => navigate(Pathname.authentication.avatar, { state: { fromVerificationCode: true } })}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(Pathname.authentication.avatar, { state: { fromVerificationCode: true } }); }}
      >
        BACK
      </div>
      <h2 className={styles.stepSubHeading}>What Brings You To Projector?</h2>
      <p className={styles.stepSubText}>Select one or more so we can personalize your experience</p>

      <div className={styles.occasionGrid}>
        {OCCASIONS.map((occasion, index) => {
          const isSelected = selectedOccasions.includes(occasion);
          return (
            <button
              key={occasion}
              type="button"
              className={`${styles.occasionButton} ${styles.buttonFadeIn}`}
              onClick={() => toggleOccasion(occasion)}
              style={{
                backgroundColor: isSelected ? '#5aa5ef' : '#4E5360',
                animationDelay: `${0.8 + index * 0.2}s`,
                position: 'relative',
              }}
            >
              {occasion}
            </button>
          );
        })}
      </div>

      {gridAnimationComplete && (
        <button
          type="button"
          className={styles.continueButton}
          onClick={handleContinue}
          disabled={selectedOccasions.length === 0}
          style={{
            opacity: selectedOccasions.length === 0 ? 0.5 : 1,
            cursor: selectedOccasions.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          Continue
        </button>
      )}
    </div>
  );
};

export default ChooseOccasion;
